import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pause, Play, ScanLine, SkipBack } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
    ASPECT_RATIOS,
    AspectRatio,
    OverlayLayer,
    VideoComposition,
    findItem,
    formatTime,
    updateItem,
} from './composition';
import { CompositionHistory } from './use-composition-history';
import { Playback } from './use-playback';
import { EditorSelection } from './Timeline';
import { LayerBox } from './render';

interface PreviewStageProps {
    comp: VideoComposition;
    playback: Playback;
    history: CompositionHistory;
    selection: EditorSelection | null;
    onSelect: (s: EditorSelection | null) => void;
    onAspect: (aspect: AspectRatio) => void;
    safeArea: boolean;
    onToggleSafeArea: () => void;
}

/** Where a phone's UI covers a 9:16 reel (Instagram/TikTok), as frame fractions. */
const REEL_SAFE = { top: 0.12, bottom: 0.22, left: 0.06, right: 0.14 };
const TITLE_SAFE = { top: 0.08, bottom: 0.08, left: 0.08, right: 0.08 };

export const PreviewStage: React.FC<PreviewStageProps> = ({
    comp,
    playback,
    history,
    selection,
    onSelect,
    onAspect,
    safeArea,
    onToggleSafeArea,
}) => {
    const { t } = useTranslation();
    const wrapRef = useRef<HTMLDivElement>(null);
    const canvasEl = useRef<HTMLCanvasElement | null>(null);
    const [size, setSize] = useState({ w: 0, h: 0 });
    const [box, setBox] = useState<LayerBox | null>(null);
    const [guides, setGuides] = useState<{ v: boolean; h: boolean }>({ v: false, h: false });
    const drag = useRef<{ id: string; startX: number; startY: number; x0: number; y0: number; base: VideoComposition; result?: VideoComposition } | null>(null);

    const bindCanvas = playback.canvasRef;
    const setCanvas = useCallback((el: HTMLCanvasElement | null) => {
        canvasEl.current = el;
        bindCanvas(el);
    }, [bindCanvas]);

    // Fit the frame inside the stage.
    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const fit = () => {
            const rect = el.getBoundingClientRect();
            const aspect = comp.width / comp.height;
            const availW = Math.max(40, rect.width - 48);
            const availH = Math.max(40, rect.height - 40);
            const w = Math.min(availW, availH * aspect);
            setSize({ w: Math.floor(w), h: Math.floor(w / aspect) });
        };
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(el);
        return () => ro.disconnect();
    }, [comp.width, comp.height]);

    // Backing store: sharp on hi-dpi screens, never more pixels than the export.
    useEffect(() => {
        const c = canvasEl.current;
        if (!c || !size.w) return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const scale = Math.min(1, (size.w * dpr) / comp.width);
        const w = Math.max(2, Math.round(comp.width * scale));
        const h = Math.max(2, Math.round(comp.height * scale));
        if (c.width !== w || c.height !== h) {
            c.width = w;
            c.height = h;
        }
        playback.engine?.requestRender();
    }, [size, comp.width, comp.height, playback.engine]);

    // The selection box follows the layer as it animates.
    const selectedLayerId = selection?.kind === 'layer' ? selection.id : null;
    useEffect(() => {
        const engine = playback.engine;
        if (!engine) return undefined;
        engine.onDraw = () => {
            const c = canvasEl.current;
            if (!selectedLayerId || !c) {
                setBox((b) => (b ? null : b));
                return;
            }
            const hb = engine.hitBoxes.get(selectedLayerId);
            if (!hb) {
                setBox((b) => (b ? null : b));
                return;
            }
            const k = size.w / c.width;
            setBox({ x: hb.x * k, y: hb.y * k, width: hb.width * k, height: hb.height * k });
        };
        engine.requestRender();
        return () => { engine.onDraw = undefined; };
    }, [playback.engine, selectedLayerId, size.w]);

    const hitTest = (clientX: number, clientY: number): string | null => {
        const engine = playback.engine;
        const c = canvasEl.current;
        if (!engine || !c) return null;
        const rect = c.getBoundingClientRect();
        const x = ((clientX - rect.left) / rect.width) * c.width;
        const y = ((clientY - rect.top) / rect.height) * c.height;
        const hits = Array.from(engine.hitBoxes.entries())
            .filter(([, b]) => x >= b.x && x <= b.x + b.width && y >= b.y && y <= b.y + b.height)
            .map(([id]) => id)
            .reverse();
        if (!hits.length) return null;
        // Type and logos win over grounds and scrims that cover the frame.
        const solid = hits.find((id) => {
            const f = findItem(comp, id);
            return f && (f.item as OverlayLayer).kind !== 'shape';
        });
        return solid ?? hits[0];
    };

    const onPointerDown = (e: React.PointerEvent) => {
        if (e.button !== 0) return;
        const id = hitTest(e.clientX, e.clientY);
        if (!id) {
            onSelect(null);
            return;
        }
        const f = findItem(comp, id);
        if (!f) return;
        onSelect({ kind: 'layer', id });
        const layer = f.item as OverlayLayer;
        drag.current = { id, startX: e.clientX, startY: e.clientY, x0: layer.x, y0: layer.y, base: history.committed() };
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        const d = drag.current;
        if (!d || !size.w) return;
        let x = d.x0 + (e.clientX - d.startX) / size.w;
        let y = d.y0 + (e.clientY - d.startY) / size.h;
        // Snap to the centre lines.
        const v = Math.abs(x - 0.5) < 0.012;
        const h = Math.abs(y - 0.5) < 0.012;
        if (v) x = 0.5;
        if (h) y = 0.5;
        setGuides({ v, h });
        d.result = updateItem(d.base, d.id, { x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000 } as any);
        history.preview(d.result);
    };

    const onPointerUp = () => {
        const d = drag.current;
        drag.current = null;
        setGuides({ v: false, h: false });
        if (d?.result) history.commit(d.result);
    };

    const safe = comp.aspect === '9:16' ? REEL_SAFE : TITLE_SAFE;

    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div
                ref={wrapRef}
                className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-muted/60 bg-[radial-gradient(circle,var(--border)_0.6px,transparent_0.6px)] bg-[length:14px_14px]"
                onPointerDown={(e) => { if (e.target === e.currentTarget) onSelect(null); }}
            >
                <div className="relative overflow-hidden rounded-md ring-1 ring-black/10" style={{ width: size.w, height: size.h }}>
                    <canvas
                        ref={setCanvas}
                        className="block size-full cursor-default"
                        style={{ width: size.w, height: size.h }}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                        data-ve-preview
                    />
                    {box && (
                        <div
                            className="pointer-events-none absolute border border-dashed border-white mix-blend-difference"
                            style={{ left: box.x - 3, top: box.y - 3, width: box.width + 6, height: box.height + 6 }}
                        />
                    )}
                    {guides.v && <div className="pointer-events-none absolute inset-y-0 start-1/2 w-px bg-sky-400/80" />}
                    {guides.h && <div className="pointer-events-none absolute inset-x-0 top-1/2 h-px bg-sky-400/80" />}
                    {safeArea && (
                        <div className="pointer-events-none absolute inset-0">
                            <div
                                className="absolute border border-dashed border-white/70"
                                style={{
                                    left: `${safe.left * 100}%`,
                                    right: `${safe.right * 100}%`,
                                    top: `${safe.top * 100}%`,
                                    bottom: `${safe.bottom * 100}%`,
                                }}
                            />
                            <div className="absolute inset-x-0 top-0 bg-black/25" style={{ height: `${safe.top * 100}%` }} />
                            <div className="absolute inset-x-0 bottom-0 bg-black/25" style={{ height: `${safe.bottom * 100}%` }} />
                            <span className="absolute start-2 top-1.5 text-[10px] font-medium text-white/85">
                                {t('VIDEO_EDITOR.PREVIEW.SAFE_ZONE')}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Transport */}
            <div className="flex h-12 shrink-0 items-center gap-2 border-t bg-background px-3">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('VIDEO_EDITOR.PREVIEW.TO_START')}
                    onClick={() => playback.seek(0)}
                    className="text-muted-foreground"
                >
                    <SkipBack />
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={playback.playing ? t('VIDEO_EDITOR.PREVIEW.PAUSE') : t('VIDEO_EDITOR.PREVIEW.PLAY')}
                    onClick={playback.toggle}
                    data-ve-play
                >
                    {playback.playing ? <Pause /> : <Play />}
                </Button>
                <span className="min-w-[110px] text-xs tabular-nums text-muted-foreground">
                    <span className="text-foreground">{formatTime(playback.time)}</span> / {formatTime(playback.duration)}
                </span>
                <div className="ms-auto flex items-center gap-1">
                    {ASPECT_RATIOS.map((a) => (
                        <button
                            key={a}
                            type="button"
                            onClick={() => onAspect(a)}
                            aria-pressed={comp.aspect === a}
                            className={cn(
                                'flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs tabular-nums transition-colors hover:bg-muted',
                                comp.aspect === a ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground',
                            )}
                        >
                            <AspectGlyph aspect={a} />
                            {a}
                        </button>
                    ))}
                    <div className="mx-1 h-5 w-px bg-border" />
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-pressed={safeArea}
                                aria-label={t('VIDEO_EDITOR.PREVIEW.SAFE_ZONE')}
                                onClick={onToggleSafeArea}
                                className={cn('text-muted-foreground', safeArea && 'bg-muted text-foreground')}
                            >
                                <ScanLine />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">{t('VIDEO_EDITOR.PREVIEW.SAFE_ZONE_HINT')}</TooltipContent>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

const AspectGlyph: React.FC<{ aspect: AspectRatio }> = ({ aspect }) => {
    const [w, h] = aspect.split(':').map(Number);
    const k = 12 / Math.max(w, h);
    return <span className="inline-block rounded-[2px] border border-current" style={{ width: Math.max(5, w * k), height: Math.max(5, h * k) }} />;
};
